import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { useU2000DeleteHandlers, type U2000DeleteHandlersParams, type U2000DeleteHandlersReturn } from "../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { useU2000GetHandlers, type U2000GetHandlersParams, type U2000GetHandlersReturn } from "../u2000Core/hooks/handler/U2000UseGetHandlers";
import type { U2000SaveHandlersReturn } from "../u2000Core/hooks/handler/U2000UseSaveHandlers";
import { useU2000SearchHandlers, type U2000SearchHandlersParams, type U2000SearchHandlersReturn } from "../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { U2000FormStateEnumEditMode } from "../u2000Core/reducer/formReducer/U2000FormState";
import { NS } from "./U3125";
import {
  buildCreateBody,
  buildDeleteBody,
  buildPaymentCreateBody,
  buildPaymentDeleteBody,
  buildPaymentUpdateBody,
  buildUpdateBody,
  doCreateData,
  doCreatePayment,
  doDeleteData,
  doDeletePayment,
  doGetCustomerCreditBalance,
  doGetData,
  doGetPdf,
  doGetWipTransactions,
  doInitCreateNewInvoice,
  doSearch,
  doSetInvoiceStatus,
  doVoidInvoice,
  doUpdateData,
  doUpdatePayment,
  luHandleAppiError,
  mapInvoiceToFormRow,
  parseCustomerCreditBalanceFromApi,
  parseWipTransactionsFromResult,
  type U3125CustomerCreditBalance,
  type U3125InitCreateNewInvoice,
  type U3125InitCreateNewInvoiceRequest,
  type U3125Invoice,
  type U3125InvoiceFormRow,
  type U3125InvoiceSaveOptions,
  type U3125InvoiceSearchRow,
  type U3125InvoiceSetStatusBody,
  type U3125InvoiceVoidBody,
} from "./U3125Service";
import type { U3134WorkInProgressSearchRow } from "../u3134WorkInProgress/U3134Service";

export function useU3125SearchHandlers<T = U3125InvoiceSearchRow>(
  params: Omit<U2000SearchHandlersParams<T>, "doSearchFn">,
): U2000SearchHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const doSearchFn = useCallback((searchPayload: Parameters<typeof doSearch>[1]) => doSearch(axios, searchPayload), [axios]);
  return useU2000SearchHandlers<T>({ ...params, doSearchFn });
}

export function useU3125GetHandlers<T extends U3125Invoice = U3125Invoice>(
  params: Omit<U2000GetHandlersParams<T>, "doGetFn">,
): U2000GetHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const doGetFn = useCallback((id: string) => doGetData(axios, id), [axios]);
  return useU2000GetHandlers<T>({ ...params, doGetFn });
}

export interface U3125SaveHandlersParams {
  onSuccess?: (row: U3125InvoiceFormRow, editMode: U2000FormStateEnumEditMode) => void;
  onError?: (errors: { error: unknown }) => void;
  onWarnings?: (warnings: string[]) => void;
}

export function useU3125SaveHandlers({
  onSuccess,
  onError,
  onWarnings,
}: U3125SaveHandlersParams): U2000SaveHandlersReturn<U3125InvoiceFormRow> {
  const axios = useU2000AxiosAuthenticated();
  const saveData = useCallback(
    async (data: U3125InvoiceFormRow, editMode: U2000FormStateEnumEditMode, saveOptions?: U3125InvoiceSaveOptions) => {
      try {
        const response =
          editMode === U2000FormStateEnumEditMode.ADD
            ? await doCreateData(axios, buildCreateBody(data, saveOptions))
            : await doUpdateData(axios, data.id, buildUpdateBody(data, saveOptions));
        const inv = response.data.result as U3125Invoice;
        const warnings = inv.warnings ?? [];
        if (warnings.length > 0) {
          onWarnings?.(warnings);
        }
        const formRow = mapInvoiceToFormRow(inv);
        onSuccess?.({ ...formRow, availableCreditSources: data.availableCreditSources ?? [] }, editMode);
      } catch (error) {
        onError?.({ error });
      }
    },
    [axios, onSuccess, onError, onWarnings],
  );
  return { saveData };
}

export function useU3125ListDeleteHandlers(
  params: Omit<U2000DeleteHandlersParams<U3125InvoiceSearchRow>, "doDeleteFn">,
): U2000DeleteHandlersReturn<U3125InvoiceSearchRow> {
  const axios = useU2000AxiosAuthenticated();
  const doDeleteFn = useCallback(
    (id: string, row: U3125InvoiceSearchRow) => doDeleteData(axios, id, { id: row.id, version: row.version ?? 0 }),
    [axios],
  );
  return useU2000DeleteHandlers<U3125InvoiceSearchRow>({ ...params, doDeleteFn });
}

export function useU3125FormDeleteHandlers(
  params: Omit<U2000DeleteHandlersParams<U3125InvoiceFormRow>, "doDeleteFn">,
): U2000DeleteHandlersReturn<U3125InvoiceFormRow> {
  const axios = useU2000AxiosAuthenticated();
  const doDeleteFn = useCallback(
    (id: string, row: U3125InvoiceFormRow) => doDeleteData(axios, id, buildDeleteBody(row)),
    [axios],
  );
  return useU2000DeleteHandlers<U3125InvoiceFormRow>({ ...params, doDeleteFn });
}

export interface U3125OpenInvoicePdfHandlersParams {
  onPopupBlocked?: () => void;
  onError?: (error: unknown) => void;
}

/** Handler hook : récupération du PDF d'une facture et ouverture dans un nouvel onglet. */
export function useU3125OpenInvoicePdfHandlers(params: U3125OpenInvoicePdfHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { onPopupBlocked, onError } = params;

  const openPdfInNewTab = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await doGetPdf(axios, id);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, "_blank", "noopener,noreferrer");
        if (!win) {
          URL.revokeObjectURL(url);
          onPopupBlocked?.();
          return false;
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
        return true;
      } catch (error: unknown) {
        onError?.(error);
        return false;
      }
    },
    [axios, onPopupBlocked, onError],
  );

  return { openPdfInNewTab };
}

export interface U3125SetInvoiceStatusHandlersParams {
  onSuccess?: (row: U3125InvoiceFormRow) => void;
  onWarnings?: (warnings: string[]) => void;
  onError?: (error: unknown) => void;
}

/** Handler hook : application d'une transition de statut sur une facture (RELEASE / CLOSE / ON_HOLD / OPEN). */
export function useU3125SetInvoiceStatusHandlers(params: U3125SetInvoiceStatusHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { onSuccess, onWarnings, onError } = params;

  const applyStatus = useCallback(
    async (id: string, body: U3125InvoiceSetStatusBody): Promise<U3125InvoiceFormRow | null> => {
      try {
        const response = await doSetInvoiceStatus(axios, id, body);
        const inv = response.data.result as U3125Invoice;
        const warnings = inv.warnings ?? [];
        if (warnings.length > 0) onWarnings?.(warnings);
        const row = mapInvoiceToFormRow(inv);
        onSuccess?.(row);
        return row;
      } catch (error: unknown) {
        onError?.(error);
        return null;
      }
    },
    [axios, onSuccess, onWarnings, onError],
  );

  return { applyStatus };
}

export interface U3125VoidInvoiceHandlersParams {
  onSuccess?: (row: U3125InvoiceFormRow) => void;
  onError?: (error: unknown) => void;
}

/** Handler hook : annulation (Void) d'une facture STANDARD RELEASE sans paiement client. */
export function useU3125VoidInvoiceHandlers(params: U3125VoidInvoiceHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { onSuccess, onError } = params;

  const voidInvoice = useCallback(
    async (id: string, body: U3125InvoiceVoidBody): Promise<U3125InvoiceFormRow | null> => {
      try {
        const response = await doVoidInvoice(axios, id, body);
        const inv = response.data.result as U3125Invoice;
        const row = mapInvoiceToFormRow(inv);
        onSuccess?.(row);
        return row;
      } catch (error: unknown) {
        onError?.(error);
        return null;
      }
    },
    [axios, onSuccess, onError],
  );

  return { voidInvoice };
}

export interface U3125GetWipTransactionsHandlersParams {
  /** Si fourni, utilisé pour les erreurs au lieu du toast par défaut. */
  onError?: (error: unknown) => void;
}

/**
 * Handler hook : recherche des transactions WIP (Work In Progress) d'un projet.
 *
 * Retourne directement les lignes parsées via `parseWipTransactionsFromResult`,
 * pour que l'appelant n'ait pas à connaître la forme exacte de la réponse.
 */
export function useU3125GetWipTransactionsHandlers(params: U3125GetWipTransactionsHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = params;

  const fetch = useCallback(
    async (projectId: string, invoiceId?: string | null): Promise<U3134WorkInProgressSearchRow[]> => {
      const pid = projectId?.trim();
      if (!pid) return [];
      try {
        const response = await doGetWipTransactions(axios, { projectId: pid, invoiceId });
        return parseWipTransactionsFromResult(response.data);
      } catch (error: unknown) {
        if (onError) {
          onError(error);
        } else {
          const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
          if (errorResult.genericErrors?.length) {
            errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
          }
        }
        return [];
      }
    },
    [axios, t, toast, onError],
  );

  return { fetch };
}

export interface U3125PaymentApiHandlersParams {
  parentFormHandlers: {
    getData: () => U3125InvoiceFormRow | null;
    setData: (row: U3125InvoiceFormRow) => void;
  };
  onError?: (error: unknown) => void;
}

/** CRUD paiements via endpoints dédiés (hors create/update facture). */
export function useU3125PaymentApiHandlers(params: U3125PaymentApiHandlersParams) {
  const axios = useU2000AxiosAuthenticated();
  const { parentFormHandlers, onError } = params;

  const applyInvoiceResult = useCallback(
    (inv: U3125Invoice) => {
      const row = mapInvoiceToFormRow(inv);
      parentFormHandlers.setData(row);
      return row;
    },
    [parentFormHandlers],
  );

  const createPayment = useCallback(
    async (payment: U3125InvoiceFormRow["payments"][number]): Promise<boolean> => {
      const invoice = parentFormHandlers.getData();
      if (!invoice?.id?.trim()) return false;
      try {
        const response = await doCreatePayment(axios, buildPaymentCreateBody(invoice, payment));
        applyInvoiceResult(response.data.result as U3125Invoice);
        return true;
      } catch (error: unknown) {
        onError?.(error);
        return false;
      }
    },
    [axios, parentFormHandlers, applyInvoiceResult, onError],
  );

  const updatePayment = useCallback(
    async (payment: U3125InvoiceFormRow["payments"][number]): Promise<boolean> => {
      if (!payment.id?.trim()) return false;
      try {
        const response = await doUpdatePayment(axios, payment.id, buildPaymentUpdateBody(payment));
        applyInvoiceResult(response.data.result as U3125Invoice);
        return true;
      } catch (error: unknown) {
        onError?.(error);
        return false;
      }
    },
    [axios, applyInvoiceResult, onError],
  );

  const deletePayments = useCallback(
    async (payments: U3125InvoiceFormRow["payments"]): Promise<boolean> => {
      try {
        let lastInvoice: U3125Invoice | null = null;
        for (const payment of payments) {
          if (!payment.id?.trim()) continue;
          const response = await doDeletePayment(axios, payment.id, buildPaymentDeleteBody(payment));
          lastInvoice = response.data.result as U3125Invoice;
        }
        if (lastInvoice) {
          applyInvoiceResult(lastInvoice);
        }
        return true;
      } catch (error: unknown) {
        onError?.(error);
        return false;
      }
    },
    [axios, applyInvoiceResult, onError],
  );

  return { createPayment, updatePayment, deletePayments };
}

export interface U3125InitCreateHandlersParams {
  onError?: (error: unknown) => void;
}

/** Handler hook : preview init create facture (`POST initCreateNewInvoice`). */
export function useU3125InitCreateHandlers(params: U3125InitCreateHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { onError } = params;

  const initCreateNewInvoice = useCallback(
    async (body: U3125InitCreateNewInvoiceRequest): Promise<U3125InitCreateNewInvoice | null> => {
      try {
        const res = await doInitCreateNewInvoice(axios, body);
        return (res.data as { result?: U3125InitCreateNewInvoice }).result ?? (res.data as unknown as U3125InitCreateNewInvoice);
      } catch (error: unknown) {
        onError?.(error);
        return null;
      }
    },
    [axios, onError],
  );

  return { initCreateNewInvoice };
}

export interface U3125CustomerCreditBalanceHandlersParams {
  onError?: (error: unknown) => void;
}

/** Handler hook : soldes crédit / acompte disponibles pour un client. */
export function useU3125CustomerCreditBalanceHandlers(params: U3125CustomerCreditBalanceHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { onError } = params;

  const fetchCustomerCreditBalance = useCallback(
    async (customerId: string): Promise<U3125CustomerCreditBalance | null> => {
      const cid = customerId?.trim();
      if (!cid) return null;
      try {
        const res = await doGetCustomerCreditBalance(axios, cid);
        return parseCustomerCreditBalanceFromApi(res.data);
      } catch (error: unknown) {
        onError?.(error);
        return null;
      }
    },
    [axios, onError],
  );

  return { fetchCustomerCreditBalance };
}
